import { Textfield, token } from "@may-i/mash-design-system";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { PageSpinner } from "~/shared/ui/PageSpinner";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import type { Branch } from "~/entities/branch/types";
import { useBranches } from "~/features/branch/hooks/use-branches";
import { usePinnedBranches } from "~/features/branch/hooks/use-pinned-items";
import { useCurrentUser } from "~/features/auth/hooks/use-current-user";
import {
  BranchRowWithVersions,
  CustomerClientHeaderRow,
  TableHeader,
} from "./BranchTable";

type FlatRow =
  | { kind: "client-header"; clientName: string }
  | { kind: "branch"; branch: Branch };

const CLIENT_HEADER_HEIGHT = 38;
const BRANCH_ROW_HEIGHT = 54;

export function CustomerBranchView() {
  const { t } = useTranslation("branch-management");
  const { data: branches = [], isPending: isBranchesPending } = useBranches();
  const { data: currentUser } = useCurrentUser();
  const { isPinned, toggle: togglePin } = usePinnedBranches();
  const [search, setSearch] = useState("");

  const filteredBranches = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = query
      ? branches.filter((b) => b.name.toLowerCase().includes(query))
      : branches;

    const pinned: Branch[] = [];
    const unpinned: Branch[] = [];
    for (const branch of filtered) {
      if (isPinned(branch.uid)) {
        pinned.push(branch);
      } else {
        unpinned.push(branch);
      }
    }
    return [...pinned, ...unpinned];
  }, [branches, search, isPinned]);

  const clientName = currentUser?.client.name;

  const flatRows = useMemo<FlatRow[]>(() => {
    const rows: FlatRow[] = [];
    if (clientName) {
      rows.push({ kind: "client-header", clientName });
    }
    for (const branch of filteredBranches) {
      rows.push({ kind: "branch", branch });
    }
    return rows;
  }, [clientName, filteredBranches]);

  const parentRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    if (!parentRef.current) return;
    const update = () => {
      if (!parentRef.current) return;
      const rect = parentRef.current.getBoundingClientRect();
      setScrollMargin(rect.top + window.scrollY);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const estimateSize = useCallback(
    (index: number) =>
      flatRows[index].kind === "client-header"
        ? CLIENT_HEADER_HEIGHT
        : BRANCH_ROW_HEIGHT,
    [flatRows],
  );

  const virtualizer = useWindowVirtualizer({
    count: flatRows.length,
    estimateSize,
    overscan: 8,
    scrollMargin,
  });

  const virtualItems = virtualizer.getVirtualItems();

  if (isBranchesPending) {
    return <Container><PageSpinner /></Container>;
  }

  return (
    <Container>
      <StickyHeader>
        <TitleRow>
          <Title>{t("title")}</Title>
        </TitleRow>

        <HeaderContents>
          <SearchInput
            size="small"
            placeholder={t("searchPlaceholderCustomer")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            startIcon="ic_search"
          />

          <TableHeader />
        </HeaderContents>
      </StickyHeader>

      <Contents>
        {filteredBranches.length > 0 ? (
          <VirtualWrapper
            ref={parentRef}
            style={{ height: `${virtualizer.getTotalSize()}px` }}
          >
            {virtualItems.map((vItem) => {
              const row = flatRows[vItem.index];
              const offset = vItem.start - virtualizer.options.scrollMargin;
              const isFirst = vItem.index === 0;
              const isLast = vItem.index === flatRows.length - 1;
              return (
                <VirtualRow
                  key={vItem.key}
                  data-index={vItem.index}
                  ref={virtualizer.measureElement}
                  style={{ transform: `translateY(${offset}px)` }}
                >
                  <RowSurface $roundTop={isFirst} $roundBottom={isLast}>
                    {row.kind === "client-header" ? (
                      <CustomerClientHeaderRow clientName={row.clientName} />
                    ) : (
                      <BranchRowWithVersions
                        branch={row.branch}
                        isPinned={isPinned(row.branch.uid)}
                        onTogglePin={togglePin}
                      />
                    )}
                  </RowSurface>
                </VirtualRow>
              );
            })}
          </VirtualWrapper>
        ) : (
          <EmptyRow>{t("empty")}</EmptyRow>
        )}
      </Contents>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const StickyHeader = styled.div`
  position: sticky;
  top: var(--gnb-height, 64px);
  z-index: 10;
  background-color: #ffffff;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px 0;
`;

const HeaderContents = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 24px 8px;
`;

const Title = styled.h2`
  margin: 0;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.56;
  color: ${token("colors.text.primary")};
`;

const Contents = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 24px 100px;
`;

const SearchInput = styled(Textfield)`
  width: 240px;

  & > div {
    padding: 4px 10px 4px 12px;
    gap: 4px;
    border-radius: 8px;
    outline: 1px solid rgba(143, 149, 178, 0.32);
    font-size: 14px;

    &:hover:not(.input__wrapper--focused) {
      outline: 1px solid rgba(143, 149, 178, 0.32);
    }
  }

  input::placeholder {
    color: #8f95b2;
  }
`;

const VirtualWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const VirtualRow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
`;

const RowSurface = styled.div<{ $roundTop: boolean; $roundBottom: boolean }>`
  background-color: #ffffff;
  border-top-left-radius: ${({ $roundTop }) => ($roundTop ? "8px" : "0")};
  border-top-right-radius: ${({ $roundTop }) => ($roundTop ? "8px" : "0")};
  border-bottom-left-radius: ${({ $roundBottom }) =>
    $roundBottom ? "8px" : "0"};
  border-bottom-right-radius: ${({ $roundBottom }) =>
    $roundBottom ? "8px" : "0"};
  overflow: hidden;
`;

const EmptyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 12px;
  font-size: 14px;
  color: ${token("colors.text.secondary")};
`;
